<template>
  <div>
    <div>
      <FilterCard :applied-filter-count="appliedFilterCount">
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="To Be Rescheduled Date"
              label-for="v-ref-no"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['order_reschedules.reschedule_date']"
                  :opens="'right'"
                  @update="
                    updateDateRange('order_reschedules.reschedule_date', $event)
                  "
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Merchant"
              label-for="merchant"
            >
              <v-select
                v-model="filters['merchant_id']"
                :options="merchantOptions"
                :reduce="(option) => option.id"
                label="name"
                placeholder="Select Merchant"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Warehouse"
              label-for="current_warehouse"
            >
              <v-select
                v-model="filters['current_warehouse']"
                :options="warehouseOptions"
                :reduce="(option) => option.id"
                label="name"
                placeholder="Select Warehouse"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </FilterCard>
    </div>
    <div>
      <b-card no-body>
        <div class="m-2">
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchOperationRescheduleNoPagination"
                  @refresh="fetchOperationReschedule"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :json_fields="json_fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div class="sortable-header">
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key ? 'ArrowUpIcon' : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(latest_reschedule.created_at)="data">
              {{ __dateTimeFormatter(data.value) }}
            </template>
            <template #cell(customer_phone)="data">
              <div>{{ data.item.customer_phone }}</div>
            </template>
            <template #cell(latest_reschedule.reschedule_date)="data">
              {{ __dateTimeFormatter(data.value).split(" ")[0] }}
            </template>
            <template #cell(cod)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label>Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1 width-125"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span
                class="text-muted pagination-text  mt-1 mt-md-0 mr-1"
              >Showing {{ meta.from }} to {{ meta.to }} of
                {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="meta.total ? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import getWarehouseList from '../ReturnOrderOriginWarehouse/WarehouseListDropdown'

const ReportRepository = RepositoryFactory.get('report')
const merchantRepository = RepositoryFactory.get('merchant')

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    FilterCard,
    vSelect,
    AdvancedDateFilter,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    FilterButtons,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      warehouseOptions: [],
      filters: {
        'order_reschedules.reschedule_date': {
          startDate: null,
          endDate: null,
        },
      },
      tableData: [],
      merchantOptions: [],
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: [
        {
          key: 'latest_reschedule.created_at',
          label: 'Reschedule Date',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'waybill_number',
          label: 'Waybill Number',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'merchant.name',
          label: 'Merchant',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'customer_name',
          label: 'Customer Name',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'customer_email',
          label: 'Customer Email',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'customer_address',
          label: 'Customer Address',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'customer_phone',
          label: 'Customer Phone',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'latest_reschedule.reschedule_date',
          label: 'To Be Rescheduled Date',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'order_current_warehouse.warehouse.name',
          label: 'Warehouse',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'cod',
          label: 'COD',
          thClass: 'text-right',
          tdClass: 'text-right',
        },
      ],
      json_fields: {
        'Reschedule Date': {
          field: 'latest_reschedule.created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Waybill Number': 'waybill_number',
        Merchant: {
          field: 'merchant.name',
          callback: value => `"${value}"`,
        },
        'Customer Name': {
          field: 'customer_name',
          callback: value => `"${value}"`,
        },
        'Customer Email': {
          field: 'customer_email',
          callback: value => `"${value}"`,
        },
        'Customer Address': {
          field: 'customer_address',
          callback: value => `"${value}"`, // Wrap the address in double quotes
        },
        'Customer Phone': 'customer_phone',
        'To Be Rescheduled Date': {
          field: 'latest_reschedule.reschedule_date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value).split(' ')[0],
        },
        Warehouse: {
          field: 'order_current_warehouse.warehouse.name',
          callback: value => `"${value}"`,
        },
        COD: 'cod',
      },
      downloadReportName: 'Active-Reschedule',
      currentDate: '',
      todayFilterQuery: '',
      items: [],
    }
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchOperationReschedule()
      },
    },
    deep: true,
    page() {
      this.fetchOperationReschedule()
    },
    perPage() {
      this.fetchOperationReschedule()
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length
    this.setTodayFilter()
    this.fetchOperationReschedule()
    this.getMerchantList()
    this.warehouseList()
    this.updateFilterCount()
  },
  methods: {
    async fetchOperationReschedule() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = await ReportRepository.getOperationsRescheduleReport(
          this.page,
          filterQuery,
          this.perPage,
        )
        // fiter the default date or selected date for table data and meta data
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchOperationRescheduleNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const {
          data,
        } = await ReportRepository.getOperationsRescheduleReportNoPagiantion(
          filterQuery,
        )
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list')
        ? JSON.parse(localStorage.getItem('merchant_list'))
        : (await merchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) {
        localStorage.setItem(
          'merchant_list',
          JSON.stringify(this.merchantOptions),
        )
      }
    },
    async warehouseList() {
      this.warehouseOptions = await getWarehouseList()
    },
    handleChangePage(page) {
      this.page = page
      this.fetchOperationReschedule()
    },
    setTodayFilter() {
      const currentDate = this.todayFilter()
      this.updateDateRange('order_reschedules.reschedule_date', currentDate)
    },
    applyFilters() {
      this.fetchOperationReschedule()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        'order_reschedules.reschedule_date': {
          startDate: null,
          endDate: null,
        },
      }
      this.setTodayFilter()
      this.$nextTick(() => {
        this.fetchOperationReschedule()
      })
      this.updateFilterCount()
    },
  },
}
</script>
<style lang="scss">
@import "../../../@core/scss/vue/libs/vue-select";
</style>
